import { api } from '~/lib/api-client';

const AGENT_ID = 'customAgent';

/**
 * Carried over from the removed test step (2026-07-16): the integrations
 * service starts every agent `inactive + unavailable` for a fresh org, and
 * without flipping these flags the chat fails with
 * `AgentNotAvailableError`. The wizard's finish is still the moment this
 * has to happen — only the screen it lived on is gone.
 */

/**
 * Read whether the `customAgent` is active+available for this tenant. Open to
 * all roles (GET /api/admin/agents is not admin-gated), so non-admins can tell
 * whether their admin still needs to switch Eddy on. Returns null if the call
 * fails — treat as "unknown", don't block onboarding.
 */
export async function isCustomAgentEnabled(): Promise<boolean | null> {
  try {
    const agents = await api.get<unknown>('/api/admin/agents');
    let list: unknown[];
    if (Array.isArray(agents)) {
      list = agents;
    } else if (agents && typeof agents === 'object') {
      list = Object.values(agents as Record<string, unknown>);
    } else {
      list = [];
    }
    const existing = list.find(
      (a): a is { id: string; active?: boolean; available?: boolean } =>
        !!a && typeof a === 'object' && (a as { id?: string }).id === AGENT_ID,
    );
    return !!existing?.active && !!existing?.available;
  } catch {
    return null;
  }
}

/**
 * Best-effort (admins only): enable `customAgent` for this tenant when the
 * wizard completes. The toggle routes are admin-gated (FRONT-145), so
 * non-admins never call this — they get a hint to ask their admin instead.
 */
export async function ensureCustomAgentEnabled(): Promise<void> {
  try {
    const enabled = await isCustomAgentEnabled();
    if (enabled === true) return;
    await api.patch(`/api/admin/agents/${AGENT_ID}/active`, { active: true });
    await api.patch(`/api/admin/agents/${AGENT_ID}/available`, { available: true });
  } catch (err) {
    console.warn('[onboarding] auto-enable customAgent failed (non-fatal):', err);
  }
}
